import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { PROGRAMS, PACKS, waLink } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const Programmes = () => {
    const { tr } = useLang();

    return (
        <div data-testid="programmes-page" className="pt-28 md:pt-36">
            <section className="pb-16 md:pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <Overline>{tr("Programmes & tarifs", "Programs & pricing")}</Overline>
                        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
                            {tr("Choisis ton", "Choose your")}<br />
                            <span className="font-accent italic normal-case font-medium text-ochre">
                                {tr("niveau d'engagement.", "level of commitment.")}
                            </span>
                        </h1>
                        <p className="mt-6 text-sand max-w-2xl leading-relaxed font-light">
                            {tr(
                                "Trois formules, une même exigence. Musculation en présentiel, coaching en ligne 100 % personnalisé via l'application Coach Roméo, ou plan nutrition de 2 mois axé sur la cuisine africaine — avec résultat garanti.",
                                "Three plans, one standard. In-person training, 100% personalized online coaching via the Coach Roméo app, or a 2-month nutrition plan built on African cuisine — with guaranteed results."
                            )}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-24 md:pb-32" data-testid="programmes-pricing">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                    {PROGRAMS.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.12} className="h-full">
                            <div
                                className={`relative h-full flex flex-col border p-8 md:p-10 transition-colors duration-500 ${
                                    p.popular
                                        ? "border-terracotta bg-obsidian-surface lg:-translate-y-4"
                                        : "border-white/10 hover:border-white/25"
                                }`}
                                data-testid={`program-card-${p.id}`}
                            >
                                {p.popular && (
                                    <span className="absolute -top-3.5 left-8 rounded-full bg-terracotta px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
                                        {tr("Le plus choisi", "Most popular")}
                                    </span>
                                )}
                                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{tr(p.name)}</h2>
                                <p className="mt-2 text-sm text-sand font-light">{tr(p.tagline)}</p>
                                <p className="mt-8">
                                    <span className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-cream">
                                        {p.price}
                                    </span>
                                    <span className="text-sand"> {tr(p.period)}</span>
                                </p>
                                <ul className="mt-8 space-y-4 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f.fr} className="flex items-start gap-3 text-sm text-cream/85">
                                            <Check className="h-4 w-4 mt-0.5 text-ochre shrink-0" strokeWidth={2.5} />
                                            {tr(f)}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={waLink(tr(
                                        `Bonjour Coach Roméo, je suis intéressé(e) par le programme ${p.name.fr} (${p.price}${p.period.fr}).`,
                                        `Hello Coach Roméo, I am interested in the ${p.name.en} program (${p.price}${p.period.en}).`
                                    ))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`program-cta-${p.id}`}
                                    className={`mt-10 inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
                                        p.popular
                                            ? "bg-terracotta text-white hover:bg-terracotta-hover"
                                            : "border border-white/25 text-cream hover:border-ochre hover:text-ochre"
                                    }`}
                                >
                                    {tr("Commencer", "Get started")} <ArrowRight className="h-4 w-4" />
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="packs-section">
                        <Overline>{tr("Séances à la salle — Gatineau & Ottawa", "Gym sessions — Gatineau & Ottawa")}</Overline>
                        <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight">
                            {tr("Des formules flexibles,", "Flexible plans,")}{" "}
                            <span className="font-accent italic font-medium text-ochre">{tr("sans frais cachés.", "no hidden fees.")}</span>
                        </h2>
                        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                            {PACKS.map((p) => (
                                <a
                                    key={p.id}
                                    href={waLink(tr(
                                        `Bonjour Coach Roméo, je veux réserver : ${p.name.fr} (${p.price}).`,
                                        `Hello Coach Roméo, I want to book: ${p.name.en} (${p.price}).`
                                    ))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`pack-cta-${p.id}`}
                                    className="group border border-white/10 bg-obsidian-surface p-6 text-center hover:border-terracotta/60 transition-colors duration-300"
                                >
                                    <p className="text-xs uppercase tracking-[0.18em] text-sand">{tr(p.name)}</p>
                                    <p className="mt-3 font-heading text-3xl md:text-4xl font-black tracking-tighter text-cream group-hover:text-terracotta transition-colors duration-300">
                                        {p.price}
                                    </p>
                                    <p className="mt-2 text-xs text-sand">{tr(p.note)}</p>
                                </a>
                            ))}
                        </div>
                        <p className="mt-4 text-sm text-sand">
                            {tr(
                                "Paiement unique, aucun frais caché — virement Interac à coachromeo7@hotmail.com.",
                                "One-time payment, no hidden fees — Interac e-transfer to coachromeo7@hotmail.com."
                            )}
                        </p>
                    </div>
                </Reveal>

                <Reveal className="mt-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="border border-ochre/30 bg-obsidian-surface p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
                            <div>
                                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
                                    {tr("Pas sûr de la formule ?", "Not sure which plan?")}
                                </h3>
                                <p className="mt-2 text-sand font-light max-w-lg">
                                    {tr(
                                        "Réserve ta consultation de 30 minutes (25 $) en visioconférence Zoom. On regarde ensemble tes objectifs et je t'oriente honnêtement.",
                                        "Book your 30-minute consultation ($25) on Zoom. We'll look at your goals together and I'll point you in the right direction."
                                    )}
                                </p>
                            </div>
                            <Link
                                to="/contact"
                                data-testid="programmes-cta-consultation"
                                className="shrink-0 inline-flex items-center justify-center gap-3 rounded-full bg-ochre px-8 py-4 text-sm font-bold uppercase tracking-wider text-obsidian transition-colors duration-300 hover:bg-ochre-hover"
                            >
                                {tr("Consultation — 25 $", "Consultation — $25")} <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Programmes;
